import React, { useState, useEffect, useRef } from 'react';

const WolfChatbot = ({ onChatToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showQuestions, setShowQuestions] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactData, setContactData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const messagesEndRef = useRef(null);

  const toggleChat = () => {
    console.log('Toggle clicked, current state:', isOpen);
    setIsOpen(prev => !prev);
  };

  useEffect(() => {
    if (onChatToggle) {
      onChatToggle(isOpen);
    }
  }, [isOpen, onChatToggle]);

  const predefinedQA = [
    {
      question: "Who is Karan Dixit?",
      answer: "I'm Karan Dixit, a passionate Full Stack Developer. I completed my BCA and I'm now pursuing my MCA (AI/ML) at Chandigarh University (2026–2028). I specialize in creating innovative web solutions with modern technologies like React, Node.js, and Firebase."
    },
    {
      question: "What are your skills?",
      answer: "My core skills include: JavaScript/TypeScript, React/Next.js, Node.js/Express, Firebase, MongoDB, AWS, and Tailwind CSS. I'm always learning new technologies!"
    },
    {
      question: "Tell me about your projects",
      answer: "I've built and shipped live products including Freelysta (freelancer management), GoForCab (cab booking), AINOR (subscription web development), and Livestayy (PG & rental management)."
    },
    {
      question: "How can I contact you?",
      answer: "You can reach me at:\n📧 dixitk941@gmail.com\n💼 linkedin.com/in/karan-dixit21\n🐙 github.com/dixitk941\n\nOr use the contact form on this site!"
    },
    {
      question: "Are you available for work?",
      answer: "Yes! I'm currently open to freelance projects and collaboration opportunities. Feel free to reach out with your project ideas."
    }
  ];

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setTimeout(() => {
        setMessages([
          {
            text: "Hey there! 👋 I'm here to help you learn more about Karan. What would you like to know?",
            sender: 'bot',
            timestamp: new Date()
          }
        ]);
        setShowQuestions(true);
      }, 400);
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleQuestionClick = (qa) => {
    const userMessage = { text: qa.question, sender: 'user', timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setShowQuestions(false);

    setTimeout(() => {
      const botMessage = { 
        text: qa.answer, 
        sender: 'bot', 
        timestamp: new Date(),
        showContactButton: qa.question === "How can I contact you?"
      };
      setMessages(prev => [...prev, botMessage]);
      setTimeout(() => setShowQuestions(true), 800);
    }, 800);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: '4a23f842-0992-4aa8-8b4c-5c89ed67160b',
          ...contactData,
          source: 'Chat Widget'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setMessages(prev => [...prev, {
          text: "Message sent! ✓ I'll get back to you within 24 hours.",
          sender: 'bot',
          timestamp: new Date()
        }]);
        setContactData({ name: '', email: '', message: '' });
        setShowContactForm(false);
        setTimeout(() => setShowQuestions(true), 500);
      } else {
        throw new Error('Failed to send');
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        text: "Oops! Something went wrong. Please try the main contact form.",
        sender: 'bot',
        timestamp: new Date()
      }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetChat = () => {
    setMessages([]);
    setShowQuestions(false);
    setShowContactForm(false);
    setTimeout(() => {
      setMessages([{ text: "Fresh start! What would you like to know?", sender: 'bot', timestamp: new Date() }]);
      setShowQuestions(true);
    }, 300);
  };

  return (
    <div id="wolf-chatbot">
      {/* Toggle Button */}
      <div className="fixed bottom-6 right-6 z-[9999]">
        <button
          onClick={toggleChat}
          className={`w-12 h-12 rounded-full border border-[var(--line)] flex items-center justify-center cursor-pointer ${
            isOpen ? 'bg-[var(--signal)] text-white' : 'bg-[var(--bg-2)] text-[var(--ink)]'
          }`}
          style={{ position: 'relative', zIndex: 9999 }}
        >
          {isOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          )}
        </button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-[360px] max-w-[calc(100vw-48px)] h-[500px] max-h-[70vh] z-40 flex flex-col overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--bg)]">
          
          {/* Header */}
          <div className="p-4 border-b border-[#1a1a1a] flex items-center justify-between bg-[#0f0f0f]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white text-black flex items-center justify-center font-bold">
                K
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Karan's Assistant</h3>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-400"></span>
                  <span className="text-xs text-gray-500">Online</span>
                </div>
              </div>
            </div>
            <button onClick={resetChat} className="p-2 rounded-lg hover:bg-[#1a1a1a] text-gray-500 hover:text-white transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm ${
                  msg.sender === 'user' 
                    ? 'bg-white text-black rounded-br-sm' 
                    : 'bg-[#161616] border border-[#222] text-gray-300 rounded-bl-sm'
                }`}>
                  <p className="whitespace-pre-line leading-relaxed">{msg.text}</p>
                </div>
              </div>
            ))}
            
            {/* Contact button */}
            {messages.some(m => m.showContactButton) && !showContactForm && (
              <button
                onClick={() => { setShowContactForm(true); setShowQuestions(false); }}
                className="pill-btn flex items-center gap-2 text-xs"
              >
                <span>Send Direct Message</span>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Contact Form */}
          {showContactForm && (
            <div className="p-4 border-t border-[#1a1a1a] bg-[#0f0f0f]">
              <form onSubmit={handleContactSubmit} className="space-y-3">
                <input
                  type="text"
                  placeholder="Name"
                  value={contactData.name}
                  onChange={e => setContactData(p => ({...p, name: e.target.value}))}
                  required
                  className="input-field text-sm py-2"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={contactData.email}
                  onChange={e => setContactData(p => ({...p, email: e.target.value}))}
                  required
                  className="input-field text-sm py-2"
                />
                <textarea
                  placeholder="Message"
                  value={contactData.message}
                  onChange={e => setContactData(p => ({...p, message: e.target.value}))}
                  required
                  rows="2"
                  className="input-field text-sm py-2 resize-none"
                />
                <div className="flex gap-2">
                  <button type="submit" disabled={isSubmitting} className="pro-btn-filled flex-1 text-xs py-2">
                    {isSubmitting ? 'Sending...' : 'Send'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => { setShowContactForm(false); setShowQuestions(true); }}
                    className="pill-btn text-xs py-2"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Questions */}
          {showQuestions && !showContactForm && (
            <div className="p-4 border-t border-[#1a1a1a] max-h-44 overflow-y-auto bg-[#0f0f0f]">
              <p className="text-[10px] uppercase tracking-wider text-gray-600 mb-3">Quick Questions</p>
              <div className="space-y-2">
                {predefinedQA.map((qa, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuestionClick(qa)}
                    className="w-full text-left p-3 rounded-xl bg-[#111] border border-[#1a1a1a] text-xs text-gray-400 hover:text-white hover:border-[#333] hover:bg-[#161616] transition-all"
                  >
                    {qa.question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Typing indicator */}
          {!showQuestions && !showContactForm && messages.length > 0 && messages[messages.length - 1].sender === 'user' && (
            <div className="p-4 border-t border-[#1a1a1a] flex items-center gap-3 bg-[#0f0f0f]">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></span>
                <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></span>
                <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></span>
              </div>
              <span className="text-xs text-gray-500">Typing...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WolfChatbot;
